import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { streamSSE } from "@/lib/sse-client";
import { getAuthToken } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { useSmartTrawlStore } from "@/stores/smart-trawl-store";
import { guideSignals } from "@/lib/guideSignals";

interface SmartTrawlOptions {
  /** Scope to a single paper's highlights (PDF viewer mode). */
  paperId?: string;
  /** Scope to foundational papers only (BuildBar mode). */
  scope?: "foundational";
}

export function useSmartTrawl(conceptId: string) {
  const qc = useQueryClient();
  const store = useSmartTrawlStore();

  const run = useCallback(async (options?: SmartTrawlOptions) => {
    const { start, updateProgress, complete, fail } = useSmartTrawlStore.getState();
    start(0);

    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const controller = new AbortController();
      const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
      const params = new URLSearchParams();
      if (options?.paperId) params.set("paper_id", options.paperId);
      if (options?.scope) params.set("scope", options.scope);
      const qs = params.toString();
      const url = `${baseUrl}/api/concepts/${conceptId}/smart-trawl/stream${qs ? `?${qs}` : ""}`;

      const STEP_FRACTIONS: Record<string, number> = {
        fetching_references: 0.1,
        resolving_citations: 0.3,
        searching_unmatched: 0.5,
        adding_papers: 0.7,
        done: 1.0,
        skipped_no_s2_id: 1.0,
        no_references: 1.0,
        no_resolvable_references: 1.0,
        llm_error: 1.0,
        no_matches: 1.0,
      };

      for await (const event of streamSSE(url, headers, controller.signal)) {
        const data = JSON.parse(event.data);

        if (event.event === "start") {
          updateProgress({ total: data.total_papers ?? 0 });
        } else if (event.event === "progress") {
          const paperIndex = data.index ?? 0;
          const totalPapers = data.total ?? 1;
          const stepFraction = STEP_FRACTIONS[data.step] ?? 0.5;
          const fractionalProgress = (paperIndex + stepFraction) / totalPapers;

          updateProgress({
            currentPaper: data.paper ?? "",
            current: Math.round(fractionalProgress * 100),
            total: 100,
            step: data.step ?? "",
          });
        } else if (event.event === "llm_hard_error") {
          // Org-wide LLM outage (usage cap, credit, auth) — the backend
          // aborted the run. Surface the provider's own message loudly and
          // persistently; a quiet "0 papers found" here masked a full
          // outage on 2026-07-17.
          const msg = data.message || "The LLM service is unavailable.";
          controller.abort();
          fail(msg);
          toast.error("Spot Trawl stopped — LLM unavailable", {
            description: msg,
            duration: Infinity,
          });
          return;
        } else if (event.event === "complete") {
          const added = data.total_added ?? 0;
          const dupes = data.total_duplicates ?? 0;
          guideSignals.mark(conceptId, 'hasUsedSmartTrawl');
          // Await the refetch BEFORE flipping isCompleted: curate.tsx's
          // auto-score effect reads allPapers synchronously on the completion
          // transition, so the new papers must be in cache first. complete()
          // last = freshness guaranteed.
          await qc.invalidateQueries({ queryKey: ["papers", conceptId] });
          await qc.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
          complete(added);
          if (added > 0) {
            toast.success(`Spot Trawl added ${added} paper${added !== 1 ? "s" : ""}`, {
              description: dupes > 0 ? `${dupes} duplicate${dupes !== 1 ? "s" : ""} skipped` : undefined,
            });
          } else {
            toast.info("Spot Trawl complete — no new papers found", {
              description: dupes > 0 ? `${dupes} duplicate${dupes !== 1 ? "s" : ""} skipped` : "Try highlighting different passages",
            });
          }
        }
      }
    } catch (_err) {
      fail("Spot Trawl failed");
    }
  }, [conceptId, qc]);

  return {
    status: store.isRunning ? "running" : store.isCompleted ? "complete" : store.error ? "error" : "idle",
    run,
  };
}
